import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authAPI, usersAPI } from '../services/api';
import './DriverRegisterPage.css';

// Compress image to target size (1MB max)
const compressImage = (file, maxSizeBytes = 1024 * 1024) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Start with original dimensions and high quality
        let quality = 0.9;
        let scale = 1;

        const compress = () => {
          // Apply scaling
          canvas.width = width * scale;
          canvas.height = height * scale;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              if (blob.size <= maxSizeBytes || (quality <= 0.1 && scale <= 0.25)) {
                // Target reached or can't compress further
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Need more compression
                if (quality > 0.1) {
                  quality -= 0.1;
                } else if (scale > 0.25) {
                  // Reset quality and reduce scale
                  quality = 0.8;
                  scale -= 0.15;
                }
                compress();
              }
            },
            'image/jpeg',
            quality
          );
        };

        compress();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

function DriverRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    license_number: '',
    can_work_in_us: false,
  });
  const [licensePlateFile, setLicensePlateFile] = useState(null);
  const [licensePlatePreview, setLicensePlatePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState('');
  const [oauthUserInfo, setOauthUserInfo] = useState(null);
  const [isOAuthFlow, setIsOAuthFlow] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a PNG, JPG, or PDF file');
        return;
      }

      // For PDFs, keep 5MB limit
      if (file.type === 'application/pdf') {
        if (file.size > 5 * 1024 * 1024) {
          setError('PDF file size must be less than 5MB');
          return;
        }
        setLicensePlateFile(file);
        setLicensePlatePreview(null);
        setError('');
        return;
      }

      // For images, compress to under 1MB
      setError('');
      setCompressing(true);

      try {
        let processedFile = file;

        // Only compress if image is over 1MB
        if (file.size > 1024 * 1024) {
          processedFile = await compressImage(file);
        }

        setLicensePlateFile(processedFile);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setLicensePlatePreview(reader.result);
        };
        reader.readAsDataURL(processedFile);
      } catch (err) {
        setError('Failed to process image. Please try a different file.');
      } finally {
        setCompressing(false);
      }
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const oauthState = sessionStorage.getItem('oauth_state');

    if (code && oauthState) {
      handleOAuthCallback(code, oauthState);
    }
  }, [location]);

  const handleGoogleSignIn = async () => {
    try {
      const redirectUri = `${window.location.origin}/register-driver`;
      const response = await authAPI.getGoogleAuthUrl(redirectUri);
      const { url, state } = response.data;

      // Store state for CSRF verification
      sessionStorage.setItem('oauth_state', state);

      // Redirect to Google
      window.location.href = url;
    } catch (err) {
      setError('Failed to initiate Google sign-in. Please try again.');
    }
  };

  const handleOAuthCallback = async (code, state) => {
    try {
      const response = await authAPI.googleOAuthCallback(code, state);
      const data = response.data;

      if (data.requires_phone) {
        // New OAuth user - pre-fill form with Google data
        setOauthUserInfo(data.user_info);
        setIsOAuthFlow(true);
        setFormData(prev => ({
          ...prev,
          first_name: data.user_info.first_name || '',
          last_name: data.user_info.last_name || '',
          email: data.user_info.email || '',
        }));
        sessionStorage.setItem('oauth_code', code);
        setError('');
      } else {
        // Existing user - redirect to driver dashboard
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code');
        navigate('/driver');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed. Please try again.');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!isOAuthFlow) {
      if (formData.password !== formData.confirm_password) {
        setError('Passwords do not match');
        return;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    if (!formData.can_work_in_us) {
      setError('You must be authorized to work in the United States');
      return;
    }

    if (!licensePlateFile) {
      setError('Please upload a photo of your license plate');
      return;
    }

    setSubmitting(true);

    try {
      if (isOAuthFlow) {
        // OAuth flow - complete OAuth registration with phone and driver info
        const code = sessionStorage.getItem('oauth_code');
        const state = sessionStorage.getItem('oauth_state');

        // Step 1: Complete OAuth callback with phone
        await authAPI.googleOAuthCallback(code, state, formData.phone);

        // Step 2: Update user to driver role with license info
        const updateData = {
          license_number: formData.license_number,
          can_work_in_us: formData.can_work_in_us,
        };
        await usersAPI.updateProfile(updateData);

        // Step 3: Upload license plate photo
        const uploadFormData = new FormData();
        uploadFormData.append('file', licensePlateFile);

        try {
          await usersAPI.uploadLicensePlate(uploadFormData);
        } catch (uploadError) {
          console.error('Error uploading license plate:', uploadError);
        }

        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code');
      } else {
        // Regular registration flow
        const registrationData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          license_number: formData.license_number,
          can_work_in_us: formData.can_work_in_us,
        };

        await authAPI.registerDriver(registrationData);

        // Upload license plate photo
        const uploadFormData = new FormData();
        uploadFormData.append('file', licensePlateFile);

        try {
          await usersAPI.uploadLicensePlate(uploadFormData);
        } catch (uploadError) {
          console.error('Error uploading license plate:', uploadError);
        }
      }

      // Show success message and redirect
      alert(
        'Driver application submitted successfully! ' +
        'Your account is pending admin approval. ' +
        'You will receive a notification once approved.'
      );
      navigate('/driver');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page driver-register-page" style={{ backgroundColor: 'black' }}>
      <div className="register-container">
        <h1>Become a Driver</h1>
        <p className="subtitle">Join our delivery team and start earning</p>

        <Link to="/register" className="driver-redirect-btn">
          ← I am a customer
        </Link>

        {!isOAuthFlow && (
          <>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
                <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>

            <div className="oauth-divider">
              <span>OR</span>
            </div>
          </>
        )}

        {isOAuthFlow && oauthUserInfo && (
          <div className="oauth-info-banner">
            Signed in with Google as <strong>{oauthUserInfo.email}</strong>
            <br/>
            Complete your driver application below
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-section">
            <h2>Personal Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  disabled={isOAuthFlow}
                  style={isOAuthFlow ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                {isOAuthFlow && <small style={{ color: '#666', marginTop: '4px' }}>From Google account</small>}
              </div>

              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  disabled={isOAuthFlow}
                  style={isOAuthFlow ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                {isOAuthFlow && <small style={{ color: '#666', marginTop: '4px' }}>From Google account</small>}
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 555-5555"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-input"
                disabled={isOAuthFlow}
                style={isOAuthFlow ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
              {isOAuthFlow && <small style={{ color: '#666', marginTop: '4px' }}>Using Google account</small>}
            </div>

            {!isOAuthFlow && (
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength="6"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Confirm Password *</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Vehicle Information</h2>

            <div className="form-group">
              <label>License Plate Number *</label>
              <input
                type="text"
                name="license_number"
                value={formData.license_number}
                onChange={handleInputChange}
                placeholder="ABC 1234"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>License Plate Photo *</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,application/pdf"
                onChange={handleFileChange}
                required
                className="form-input"
                disabled={compressing}
              />
              <small>Upload a clear photo of your license plate (PNG, JPG, or PDF)</small>

              {compressing && (
                <div className="file-info" style={{ color: '#007bff' }}>
                  Compressing image...
                </div>
              )}
              {licensePlatePreview && (
                <div className="image-preview">
                  <img src={licensePlatePreview} alt="License plate preview" />
                </div>
              )}
              {licensePlateFile && !licensePlatePreview && !compressing && (
                <div className="file-info">
                  Selected: {licensePlateFile.name}
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h2>Work Authorization</h2>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="can_work_in_us"
                  checked={formData.can_work_in_us}
                  onChange={handleInputChange}
                  required
                />
                <span>I am legally authorized to work in the United States *</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={submitting || compressing}
              className="btn btn-large"
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                backgroundImage: 'none',
                opacity: 1
              }}
            >
              {submitting ? 'Submitting Application...' : compressing ? 'Processing Image...' : 'Submit Driver Application'}
            </button>
          </div>

          <div className="form-footer">
            <p>
              Already have an account? <Link to="/login">Log in</Link>
            </p>
            <p>
              Want to register as a customer? <Link to="/register">Customer Registration</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverRegisterPage;
